import { useEffect, useEffectEvent, useMemo, useRef, useState } from "react";
import { formatISO, parseISO } from "date-fns";
import {
    AccountingReportComparison,
    AccountingReportComparisonDiff,
    AccountingReportRatioType,
    AccountingReportCycleOptionType,
} from "@common/constants";
import { DateRange } from "@common/helpers/date";
import { resolveEffectiveReportCycle } from "@common/helpers/reports";
import {
    SpreadsheetViewConfig,
    SpreadsheetReference,
} from "@common/schemas/spreadsheetReference.schema";
import { SpreadsheetRowData } from "@components/spreadsheet/types";
import { useSpreadsheetRefContextMaybe } from "@components/tasks/SpreadsheetRefContext";

interface ComparisonOptions {
    comparisons: AccountingReportComparison[];
    comparisonDiffs: AccountingReportComparisonDiff[];
    ratioTypes: AccountingReportRatioType[];
}

interface UseViewConfigSyncParams {
    localDateRange: Partial<DateRange>;
    localCycle: AccountingReportCycleOptionType;
    comparisonOptions: ComparisonOptions;
    setLocalDateRange: (range: Partial<DateRange>) => void;
    setLocalCycle: (cycle: AccountingReportCycleOptionType) => void;
    setComparisonOptions: (options: ComparisonOptions) => void;
    rows: SpreadsheetRowData[] | null;
    extraViewConfig?: Partial<SpreadsheetViewConfig>;
}

interface UseViewConfigSyncResult {
    viewConfig: SpreadsheetViewConfig;
    refCtx: ReturnType<typeof useSpreadsheetRefContextMaybe>;
}

export function useViewConfigSync({
    localDateRange,
    localCycle,
    comparisonOptions,
    setLocalDateRange,
    setLocalCycle,
    setComparisonOptions,
    rows,
    extraViewConfig,
}: UseViewConfigSyncParams): UseViewConfigSyncResult {
    const refCtx = useSpreadsheetRefContextMaybe();
    const pendingApplyRef = useRef<SpreadsheetReference | null>(null);
    // Monotonic counter that pairs each apply request with its data response,
    // preventing background React Query refetches from consuming the pending ref.
    const [applyNonce, setApplyNonce] = useState(0);
    const appliedNonceRef = useRef(0);

    const viewConfig = useMemo(
        () => ({
            periodStart: localDateRange.start
                ? formatISO(localDateRange.start, { representation: "date" })
                : undefined,
            periodEnd: localDateRange.end
                ? formatISO(localDateRange.end, { representation: "date" })
                : undefined,
            cycle: resolveEffectiveReportCycle({
                cycle: localCycle,
            }),
            comparisons: comparisonOptions.comparisons,
            comparisonDiffs: comparisonOptions.comparisonDiffs,
            ratioTypes: comparisonOptions.ratioTypes,
            ...extraViewConfig,
        }),
        [localDateRange, localCycle, comparisonOptions, extraViewConfig],
    );

    // Publish current view config to context so message chips can compare.
    const publishViewConfig = useEffectEvent(
        (config: SpreadsheetViewConfig) => {
            refCtx?.setCurrentViewConfig(config);
            return () => {
                refCtx?.setCurrentViewConfig(null);
            };
        },
    );
    useEffect(() => publishViewConfig(viewConfig), [viewConfig]);

    // Register the apply-view-config handler so clicking "View cells" in the
    // popover restores the snapshot and highlights the referenced cells.
    const registerApplyViewConfigHandler = useEffectEvent(() => {
        if (!refCtx) {
            return;
        }
        const handler = (ref: SpreadsheetReference) => {
            const cfg = ref.viewConfig;
            if (!cfg) {
                return;
            }
            if (cfg.periodStart !== undefined || cfg.periodEnd !== undefined) {
                setLocalDateRange({
                    start: cfg.periodStart
                        ? parseISO(cfg.periodStart)
                        : undefined,
                    end: cfg.periodEnd ? parseISO(cfg.periodEnd) : undefined,
                });
            }
            if (cfg.cycle !== undefined) {
                setLocalCycle(
                    resolveEffectiveReportCycle({
                        cycle: cfg.cycle,
                    }),
                );
            }
            if (
                cfg.comparisons !== undefined ||
                cfg.comparisonDiffs !== undefined ||
                cfg.ratioTypes !== undefined
            ) {
                setComparisonOptions({
                    comparisons: cfg.comparisons ?? [],
                    comparisonDiffs: cfg.comparisonDiffs ?? [],
                    ratioTypes: cfg.ratioTypes ?? [],
                });
            }
            pendingApplyRef.current = ref;
            setApplyNonce((n) => n + 1);
        };
        refCtx.registerApplyViewConfigHandler(handler);
        return () => refCtx.registerApplyViewConfigHandler(null);
    });
    useEffect(() => registerApplyViewConfigHandler(), []);

    // After data refetches following a programmatic view-config restore,
    // highlight the referenced cells. The nonce guard ensures background
    // React Query refetches don't consume the pending ref prematurely.
    useEffect(() => {
        const pending = pendingApplyRef.current;
        if (!pending || !rows || !refCtx) {
            return;
        }
        if (appliedNonceRef.current === applyNonce) {
            return;
        }
        appliedNonceRef.current = applyNonce;
        refCtx.focusSpreadsheetReferences([pending]);
        pendingApplyRef.current = null;
    }, [rows, refCtx, applyNonce]);

    return { viewConfig, refCtx };
}
